/**
 * WallDesignPage — reinforced concrete structural wall (placa).
 *
 * Left rail (inputs): section + materials + loads + reinforcement.
 * Center: cross-section (top view) + closed P-M interaction curve.
 * Right rail: geometry summary, DCR, shear, boundary elements, verifications.
 */

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useWallStore } from '@/features/muros/store';
import { useWallAnalysis } from '@/features/muros/useWallAnalysis';
import { useExportWall } from '@/features/muros/useExport';
import { Wall3DView } from '@/features/muros/Wall3DView';
import { RhoBar } from '@/components/ui/RhoBar';
import { ExportCostHint } from '@/features/billing/ExportCostHint';
import { DesignToolbar } from '@/features/designs/DesignToolbar';
import { SaveDesignDialog } from '@/features/designs/SaveDesignDialog';
import { useDesign } from '@/features/designs/useDesigns';
import type { RebarDiam, WallAnalyzeRequest } from '@/features/muros/types';

const REBAR_DIAMS: RebarDiam[] = ['3/8', '1/2', '5/8', '3/4', '1', '1 3/8'];

export function WallDesignPage() {
  const request = useWallStore((s) => s.request);
  const setSection = useWallStore((s) => s.setSection);
  const setMaterials = useWallStore((s) => s.setMaterials);
  const setLoads = useWallStore((s) => s.setLoads);
  const setReinforcement = useWallStore((s) => s.setReinforcement);
  const loadRequest = useWallStore((s) => s.loadRequest);

  // URL hydration: /app/walls/{id} loads a saved design into the store.
  const params = useParams<{ id?: string }>();
  const designId =
    params.id && params.id !== 'new' && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const designQuery = useDesign(designId);
  useEffect(() => {
    if (designQuery.data) {
      loadRequest(designQuery.data.payload as unknown as WallAnalyzeRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designQuery.data?.id]);
  const [saveOpen, setSaveOpen] = useState(false);

  const { data, isFetching, isError, error } = useWallAnalysis(request);
  const exporter = useExportWall();
  const [view, setView] = useState<'2D' | '3D'>('2D');

  // Hidden 3D for PDF snapshot capture (always mounted off-screen).
  const snapshotContainerRef = useRef<HTMLDivElement | null>(null);
  const captureSnapshot = (): string | null => {
    const el = snapshotContainerRef.current?.querySelector('canvas') as HTMLCanvasElement | null;
    if (!el) return null;
    try {
      return el.toDataURL('image/png');
    } catch (e) {
      console.warn('[wall-3d] toDataURL failed:', e);
      return null;
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header
        className="flex h-14 shrink-0 items-center gap-6 border-b px-6"
        style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
            Software E.060
          </div>
          <div className="text-sm font-semibold leading-none" style={{ color: 'var(--text-primary)' }}>
            Diseño de muro estructural · Placa de concreto armado
          </div>
        </div>
        {/* Right side intentionally empty — DesignToolbar below handles status + exports. */}
      </header>

      <DesignToolbar
        status={isError ? 'error' : isFetching ? 'busy' : 'ready'}
        statusLabel={isError ? `Error: ${String(error)}` : undefined}
        onExportPdf={() => exporter.trigger('pdf', request, captureSnapshot())}
        onExportXlsx={() => exporter.trigger('xlsx', request, captureSnapshot())}
        busyExport={exporter.busy}
        onSave={() => setSaveOpen(true)}
        isUpdate={!!designId}
      />
      <SaveDesignDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        module="muros"
        payload={request as unknown as Record<string, unknown>}
        existing={designQuery.data ?? null}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* LEFT — Inputs */}
        <aside
          className="flex w-[300px] shrink-0 flex-col gap-3 overflow-y-auto border-r p-3 text-[11px]"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          <Group title="Sección">
            <NumberRow label="lw (cm) — longitud" value={request.section.lw} onChange={(v) => setSection({ lw: v })} step={10} />
            <NumberRow label="tw (cm) — espesor" value={request.section.tw} onChange={(v) => setSection({ tw: v })} step={1} />
            <NumberRow label="hw (cm) — altura" value={request.section.hw} onChange={(v) => setSection({ hw: v })} step={10} />
            <NumberRow label="lbe (cm) — elemento de borde" value={request.section.lbe} onChange={(v) => setSection({ lbe: v })} step={5} />
          </Group>

          <Group title="Materiales">
            <NumberRow label="f'c (kgf/cm²)" value={request.materials.fc} onChange={(v) => setMaterials({ fc: v })} step={10} />
            <NumberRow label="fy (kgf/cm²)" value={request.materials.fy} onChange={(v) => setMaterials({ fy: v })} step={100} />
            <NumberRow label="fyt (kgf/cm²)" value={request.materials.fyt} onChange={(v) => setMaterials({ fyt: v })} step={100} />
            <NumberRow label="Recubrimiento (cm)" value={request.materials.cover} onChange={(v) => setMaterials({ cover: v })} step={0.5} />
          </Group>

          <Group title="Cargas factoradas">
            <NumberRow label="Pu (kgf) — + compresión" value={request.loads.Pu} onChange={(v) => setLoads({ Pu: v })} step={1000} />
            <NumberRow label="Mu (kgf·cm)" value={request.loads.Mu} onChange={(v) => setLoads({ Mu: v })} step={100000} />
            <NumberRow label="Vu (kgf)" value={request.loads.Vu} onChange={(v) => setLoads({ Vu: v })} step={1000} />
          </Group>

          <Group title="Elemento de borde (BE)">
            <SelectRow
              label="Diámetro barras"
              value={request.reinforcement.diam_be}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_be: v as RebarDiam })}
            />
            <NumberRow label="Barras a lo largo de lbe" value={request.reinforcement.n_be_cols} onChange={(v) => setReinforcement({ n_be_cols: Math.max(2, Math.round(v)) })} step={1} />
            <NumberRow label="Filas a lo largo de tw" value={request.reinforcement.n_be_rows} onChange={(v) => setReinforcement({ n_be_rows: Math.max(1, Math.round(v)) })} step={1} />
            <SelectRow
              label="Diám. estribo confin."
              value={request.reinforcement.diam_stirrup_be}
              options={REBAR_DIAMS.slice(0, 3).map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_stirrup_be: v as RebarDiam })}
            />
            <NumberRow label="s estribo BE (cm)" value={request.reinforcement.s_stirrup_be} onChange={(v) => setReinforcement({ s_stirrup_be: v })} step={1} />
          </Group>

          <Group title="Malla vertical">
            <SelectRow
              label="Diámetro"
              value={request.reinforcement.diam_v}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_v: v as RebarDiam })}
            />
            <NumberRow label="Espaciamiento s_v (cm)" value={request.reinforcement.s_v} onChange={(v) => setReinforcement({ s_v: v })} step={1} />
            <SelectRow
              label="Cortinas"
              value={String(request.reinforcement.n_layers)}
              options={[{ value: '1', label: '1 (simple)' }, { value: '2', label: '2 (doble)' }]}
              onChange={(v) => setReinforcement({ n_layers: parseInt(v, 10) as 1 | 2 })}
            />
          </Group>

          <Group title="Malla horizontal">
            <SelectRow
              label="Diámetro"
              value={request.reinforcement.diam_h}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_h: v as RebarDiam })}
            />
            <NumberRow label="Espaciamiento s_h (cm)" value={request.reinforcement.s_h} onChange={(v) => setReinforcement({ s_h: v })} step={1} />
          </Group>
        </aside>

        {/* CENTER */}
        <main className="flex-1 overflow-auto p-4" style={{ background: 'var(--surface-canvas)' }}>
          {data && (
            <div className="flex flex-col gap-4">
              <DiagramCard
                title={view === '3D' ? 'Vista 3D — paño completo' : 'Sección transversal (planta)'}
                toolbar={
                  <div className="flex overflow-hidden rounded-[var(--radius-sm)] border" style={{ borderColor: 'var(--border-default)' }}>
                    {(['2D', '3D'] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setView(m)}
                        className="h-7 px-3 text-[10px] font-semibold tracking-[0.12em]"
                        style={{
                          background: view === m ? 'var(--accent)' : 'transparent',
                          color: view === m ? 'var(--surface-base)' : 'var(--text-secondary)',
                        }}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                }
              >
                {view === '3D' ? (
                  <div style={{ width: 880, height: 500 }}>
                    <Wall3DView
                      lw={request.section.lw}
                      tw={request.section.tw}
                      hw={request.section.hw}
                      lbe={request.section.lbe}
                      cover={request.materials.cover}
                      reinforcement={request.reinforcement}
                    />
                  </div>
                ) : (
                  <WallSectionDiagram
                    lw={request.section.lw}
                    tw={request.section.tw}
                    lbe={request.section.lbe}
                    cover={request.materials.cover}
                    reinforcement={request.reinforcement}
                  />
                )}
              </DiagramCard>

              <DiagramCard title="Diagrama de interacción P-M (en el plano)">
                <PMDiagram
                  Pn={data.pm_curve.Pn}
                  Mn={data.pm_curve.Mn}
                  phi_Pn={data.pm_curve.phi_Pn}
                  phi_Mn={data.pm_curve.phi_Mn}
                  Po={data.pm_curve.Po}
                  Pu={request.loads.Pu}
                  Mu={request.loads.Mu}
                  DCR={data.dcr.DCR}
                />
              </DiagramCard>
            </div>
          )}
        </main>

        {/* RIGHT — Results */}
        <aside
          className="flex w-[340px] shrink-0 flex-col gap-3 overflow-y-auto border-l p-3 text-[11px]"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          {data && (
            <>
              <ResultCard title="Geometría y cuantías">
                <KV label="Ag" value={`${data.geometry.Ag.toFixed(0)} cm²`} />
                <KV label="As total" value={`${data.geometry.Ast_total.toFixed(2)} cm²`} />
                <KV label="As BE c/u" value={`${data.geometry.Ast_be_each.toFixed(2)} cm²`} />
                <KV label="As malla" value={`${data.geometry.Ast_mesh.toFixed(2)} cm²`} />
                <KV label="d = 0.8·lw" value={`${data.geometry.d_effective.toFixed(1)} cm`} />
                <KV label="hw / lw" value={data.geometry.hw_lw_ratio.toFixed(3)} />
                <KV label="s_max permitido" value={`${data.geometry.s_max_allowed.toFixed(1)} cm`} />
                <div className="mt-3 space-y-3">
                  <RhoBar
                    title="ρ total (As / Ag) — E.060 limita 0.25%–6%"
                    rho={data.geometry.rho_total}
                    rho_min={0.0025}
                    rho_max={0.06}
                    rho_rec={0.04}
                    hideAdvice
                  />
                  <RhoBar
                    title="ρ_v malla vertical — mínimo E.060 11.10.10"
                    rho={data.geometry.rho_v_mesh}
                    rho_min={data.geometry.rho_v_min}
                    rho_max={0.04}
                    hideAdvice
                  />
                  <RhoBar
                    title="ρ_h malla horizontal — mínimo E.060 11.10.10"
                    rho={data.geometry.rho_h_mesh}
                    rho_min={data.geometry.rho_h_min}
                    rho_max={0.04}
                    hideAdvice
                  />
                </div>
              </ResultCard>

              <ResultCard title="Flexo-compresión (P-M)">
                <KV label="Pu" value={`${(data.dcr.Pu / 1000).toFixed(1)} tonf`} />
                <KV label="Mu" value={`${(data.dcr.Mu / 1e5).toFixed(2)} tonf·m`} />
                <KV label="D/C ratio" value={data.dcr.DCR.toFixed(3)} mono />
                <div className="mt-1 text-[10px]" style={{ color: data.dcr.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.dcr.ok ? '✓ ' : '✗ '}{data.dcr.notes}
                </div>
              </ResultCard>

              <ResultCard title="Cortante en el plano (E.060 11.10)">
                <KV label="Vu" value={`${(data.shear.Vu / 1000).toFixed(2)} tonf`} />
                <KV label="α_c" value={data.shear.alpha_c.toFixed(3)} />
                <KV label="Vc" value={`${(data.shear.Vc / 1000).toFixed(1)} tonf`} />
                <KV label="Vs" value={`${(data.shear.Vs / 1000).toFixed(1)} tonf`} />
                <KV label="φVn" value={`${(data.shear.phi_Vn / 1000).toFixed(1)} tonf`} />
                <KV label="φVn,max" value={`${(data.shear.phi_Vn_max / 1000).toFixed(1)} tonf`} />
                <KV label="ρ_h requerido" value={`${(data.shear.rho_h_req * 100).toFixed(3)} %`} />
                <div className="mt-1 text-[10px]" style={{ color: data.shear.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.shear.ok ? '✓ ' : '✗ '}{data.shear.note}
                </div>
              </ResultCard>

              <ResultCard title="Elementos de borde (E.060 21.9)">
                <KV label="¿Se requieren?" value={data.boundary.required ? 'SÍ' : 'NO'} />
                <KV label="c (eje neutro)" value={`${data.boundary.c_neutral_axis.toFixed(1)} cm`} />
                <KV label="c límite" value={`${data.boundary.c_limit.toFixed(1)} cm`} />
                {data.boundary.required && (
                  <>
                    <KV label="lbe requerido" value={`${data.boundary.lbe_required.toFixed(1)} cm`} />
                    <KV label="lbe provisto" value={`${data.boundary.lbe_provided.toFixed(1)} cm`} />
                    <KV label="s estribo máx" value={`${data.boundary.s_stirrup_required.toFixed(1)} cm`} />
                  </>
                )}
                <div className="mt-1 text-[10px]" style={{ color: data.boundary.required && (data.boundary.lbe_provided < data.boundary.lbe_required || !data.boundary.confinement_ok) ? 'var(--danger)' : 'var(--success)' }}>
                  {data.boundary.note}
                </div>
              </ResultCard>

              <ResultCard title="Verificaciones">
                {data.verifications.map((v) => (
                  <div key={v.id} className="flex items-start gap-2 py-0.5">
                    <span
                      className="mt-0.5"
                      style={{ color: v.ok ? 'var(--success)' : 'var(--danger)', fontWeight: 700, minWidth: 12 }}
                    >
                      {v.ok ? '✓' : '✗'}
                    </span>
                    <span className="flex-1 text-[10px]" style={{ lineHeight: 1.35 }}>
                      {v.label}
                    </span>
                  </div>
                ))}
              </ResultCard>
            </>
          )}
        </aside>
      </div>

      {/* HIDDEN 3D for future PDF snapshot capture */}
      {data && (
        <div
          ref={snapshotContainerRef}
          aria-hidden="true"
          style={{
            position: 'fixed',
            top: 0,
            left: -2400,
            width: 1100,
            height: 600,
            pointerEvents: 'none',
            opacity: 0,
          }}
        >
          <Wall3DView
            lw={request.section.lw}
            tw={request.section.tw}
            hw={request.section.hw}
            lbe={request.section.lbe}
            cover={request.materials.cover}
            reinforcement={request.reinforcement}
          />
        </div>
      )}
    </div>
  );
}

/* ====================================================================== */
/*  Inputs                                                                  */
/* ====================================================================== */

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-2" style={{ borderColor: 'var(--border-default)' }}>
      <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function NumberRow({ label, value, onChange, step = 1 }: { label: string; value: number; onChange: (v: number) => void; step?: number }) {
  return (
    <label className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (!Number.isNaN(v)) onChange(v);
        }}
        className="w-[120px] rounded-[var(--radius-sm)] border px-2 py-1 text-right text-[11px] font-mono"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      />
    </label>
  );
}

function SelectRow({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-[120px] rounded-[var(--radius-sm)] border px-2 py-1 text-[11px]"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

/* ====================================================================== */
/*  Results cards                                                           */
/* ====================================================================== */

function ResultCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-2"
      style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}>
      <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function KV({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className={mono ? 'font-mono text-[10.5px]' : 'text-[10.5px]'} style={{ color: 'var(--text-primary)' }}>
        {value}
      </span>
    </div>
  );
}

function DiagramCard({
  title,
  children,
  toolbar,
}: {
  title: string;
  children: React.ReactNode;
  toolbar?: React.ReactNode;
}) {
  return (
    <div className="rounded-[var(--radius-md)] border p-3"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
          {title}
        </div>
        {toolbar}
      </div>
      <div className="flex justify-center">{children}</div>
    </div>
  );
}

/* ====================================================================== */
/*  CROSS-SECTION DIAGRAM (top view of the wall)                            */
/* ====================================================================== */

const DIAM_INCH_TO_CM: Record<string, number> = {
  '3/8': 0.9525,
  '1/2': 1.27,
  '5/8': 1.5875,
  '3/4': 1.905,
  '1': 2.54,
  '1 3/8': 3.4925,
};

function WallSectionDiagram({
  lw,
  tw,
  lbe,
  cover,
  reinforcement,
}: {
  lw: number;
  tw: number;
  lbe: number;
  cover: number;
  reinforcement: {
    diam_be: string;
    n_be_cols: number;
    n_be_rows: number;
    diam_v: string;
    s_v: number;
    n_layers: number;
    diam_stirrup_be: string;
  };
}) {
  // Walls are typically very slender in plan (lw:tw ratios of 10:1 to 25:1),
  // so drawing at true aspect ratio leaves the section as a tiny strip on a
  // huge canvas. We use INDEPENDENT x/y scales and cap the apparent aspect
  // ratio at ~8:1 — this exaggerates tw vertically only when the wall is
  // slender, keeping the bars and reinforcement clearly visible.
  const W = 1080;
  const PAD_X = 48;
  const PAD_TOP = 36;
  const PAD_BOT = 60;
  const drawW = W - 2 * PAD_X;
  const scaleX = drawW / lw;
  const hpxTrue = tw * scaleX;
  const MAX_DISPLAY_RATIO = 8;          // lw_px / hpx
  const minHpxForAspect = drawW / MAX_DISPLAY_RATIO;
  const hpx = Math.max(hpxTrue, minHpxForAspect, 110);
  const scaleY = hpx / tw;
  const exaggerated = hpx > hpxTrue * 1.05;

  const wpx = lw * scaleX;
  const xL = (W - wpx) / 2;
  const yT = PAD_TOP;
  const yB = yT + hpx;
  const H = yB + PAD_BOT;
  const lbePx = lbe * scaleX;

  const d_stirrup = DIAM_INCH_TO_CM[reinforcement.diam_stirrup_be] ?? 0.9525;
  const d_be = DIAM_INCH_TO_CM[reinforcement.diam_be] ?? 1.5875;
  const d_v = DIAM_INCH_TO_CM[reinforcement.diam_v] ?? 0.9525;

  // Offsets in cm — applied with the matching axis scale.
  const offX = cover + d_stirrup + d_be / 2;
  const offY = cover + d_stirrup + d_be / 2;

  // BE rectangles
  const beLeftX1 = xL;
  const beLeftX2 = xL + lbePx;
  const beRightX1 = xL + wpx - lbePx;

  // BE bar Y positions (across tw) — use scaleY
  let beYs: number[] = [];
  if (reinforcement.n_be_rows === 1) {
    beYs = [(yT + yB) / 2];
  } else if (reinforcement.n_be_rows === 2) {
    beYs = [yT + offY * scaleY, yB - offY * scaleY];
  } else {
    const usable = hpx - 2 * offY * scaleY;
    beYs = Array.from({ length: reinforcement.n_be_rows }, (_, i) =>
      yT + offY * scaleY + (i * usable) / (reinforcement.n_be_rows - 1),
    );
  }
  // BE bar X positions inside each lbe — use scaleX
  const beXLocal: number[] =
    reinforcement.n_be_cols === 1
      ? [lbePx / 2]
      : Array.from({ length: reinforcement.n_be_cols }, (_, i) =>
          offX * scaleX + (i * (lbePx - 2 * offX * scaleX)) / (reinforcement.n_be_cols - 1),
        );

  // Mesh bars (between BEs)
  const middleStart = beLeftX2;
  const middleEnd = beRightX1;
  const middleLen = middleEnd - middleStart;
  const meshCount = Math.max(0, Math.floor(middleLen / (reinforcement.s_v * scaleX)) + 1);
  const firstMesh = middleStart + (middleLen - (meshCount - 1) * reinforcement.s_v * scaleX) / 2;
  const offVY = cover + d_stirrup + d_v / 2;
  const meshYs =
    reinforcement.n_layers === 1
      ? [(yT + yB) / 2]
      : [yT + offVY * scaleY, yB - offVY * scaleY];

  // Bar radii — keep visually consistent (not derived from true scale so
  // bars don't disappear when the section is wide).
  const r_be = 5;
  const r_v = 3.5;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: 1080 }}>
      <defs>
        <pattern id="hatch-wall" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.20" strokeWidth="1" />
        </pattern>
      </defs>

      {/* Concrete outline */}
      <rect x={xL} y={yT} width={wpx} height={hpx}
        fill="url(#hatch-wall)" stroke="var(--concrete-outline)" strokeWidth="1.4" />

      {/* BE shaded rectangles */}
      <rect x={beLeftX1} y={yT} width={lbePx} height={hpx}
        fill="rgba(245,158,11,0.10)" stroke="var(--accent)" strokeWidth="1" strokeDasharray="4 3" />
      <rect x={beRightX1} y={yT} width={lbePx} height={hpx}
        fill="rgba(245,158,11,0.10)" stroke="var(--accent)" strokeWidth="1" strokeDasharray="4 3" />

      {/* BE stirrup outline (rectangle around the BE bars) */}
      {[beLeftX1, beRightX1].map((x0, i) => (
        <rect
          key={`stirr-${i}`}
          x={x0 + (cover + d_stirrup / 2) * scaleX}
          y={yT + (cover + d_stirrup / 2) * scaleY}
          width={lbePx - 2 * (cover + d_stirrup / 2) * scaleX}
          height={hpx - 2 * (cover + d_stirrup / 2) * scaleY}
          fill="none"
          stroke="#0891B2"
          strokeWidth="1.3"
        />
      ))}

      {/* BE bars (left + right) */}
      {[beLeftX1, beRightX1].map((x0, sideIdx) => (
        <g key={`be-${sideIdx}`}>
          {beXLocal.map((dx, i) =>
            beYs.map((y, j) => (
              <circle
                key={`be-${sideIdx}-${i}-${j}`}
                cx={x0 + dx}
                cy={y}
                r={r_be}
                fill="var(--accent)"
                stroke="var(--concrete-outline)"
                strokeWidth="0.6"
              />
            )),
          )}
        </g>
      ))}

      {/* Mesh vertical bars */}
      {meshCount > 0 && Array.from({ length: meshCount }).map((_, i) => {
        const x = firstMesh + i * reinforcement.s_v * scaleX;
        return meshYs.map((y, j) => (
          <circle
            key={`mesh-${i}-${j}`}
            cx={x}
            cy={y}
            r={r_v}
            fill="var(--brand-violet, #8B5CF6)"
            stroke="var(--concrete-outline)"
            strokeWidth="0.5"
          />
        ));
      })}

      {/* BE labels */}
      <text x={beLeftX1 + lbePx / 2} y={yT - 8} textAnchor="middle" fontSize="11" fontFamily="monospace" fill="var(--accent)" style={{ fontWeight: 700, letterSpacing: '0.05em' }}>
        BE
      </text>
      <text x={beRightX1 + lbePx / 2} y={yT - 8} textAnchor="middle" fontSize="11" fontFamily="monospace" fill="var(--accent)" style={{ fontWeight: 700, letterSpacing: '0.05em' }}>
        BE
      </text>
      <text x={(middleStart + middleEnd) / 2} y={yT - 8} textAnchor="middle" fontSize="10" fontFamily="monospace" fill="var(--text-muted)">
        malla distribuida
      </text>

      {/* Dimension lines (light) — lw along the bottom */}
      <g stroke="var(--text-muted)" strokeWidth="0.8" opacity="0.7">
        <line x1={xL} y1={yB + 12} x2={xL + wpx} y2={yB + 12} />
        <line x1={xL} y1={yB + 8} x2={xL} y2={yB + 16} />
        <line x1={xL + wpx} y1={yB + 8} x2={xL + wpx} y2={yB + 16} />
      </g>
      <text x={W / 2} y={yB + 30} textAnchor="middle" fontSize="12" fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        lw = {lw} cm  ·  tw = {tw} cm  ·  lbe = {lbe} cm (cada extremo)
      </text>
      {exaggerated && (
        <text x={W / 2} y={yB + 46} textAnchor="middle" fontSize="9.5" fontFamily="monospace" fill="var(--text-muted)" style={{ fontStyle: 'italic' }}>
          (Escala vertical exagerada para visualización · escala real {(lw / tw).toFixed(0)} : 1)
        </text>
      )}

      {/* Legend */}
      <g transform={`translate(${PAD_X - 12}, ${PAD_TOP - 14})`}>
        <circle cx="6" cy="0" r="5" fill="var(--accent)" stroke="var(--concrete-outline)" strokeWidth="0.6" />
        <text x="16" y="4" fontSize="10.5" fill="var(--text-secondary)" fontFamily="monospace">
          acero BE Ø{reinforcement.diam_be}"
        </text>
        <circle cx="155" cy="0" r="3.5" fill="var(--brand-violet, #8B5CF6)" stroke="var(--concrete-outline)" strokeWidth="0.5" />
        <text x="165" y="4" fontSize="10.5" fill="var(--text-secondary)" fontFamily="monospace">
          malla Ø{reinforcement.diam_v}"
        </text>
        <line x1="275" y1="-4" x2="290" y2="-4" stroke="#0891B2" strokeWidth="1.5" />
        <line x1="290" y1="-4" x2="290" y2="4" stroke="#0891B2" strokeWidth="1.5" />
        <line x1="290" y1="4" x2="275" y2="4" stroke="#0891B2" strokeWidth="1.5" />
        <line x1="275" y1="4" x2="275" y2="-4" stroke="#0891B2" strokeWidth="1.5" />
        <text x="298" y="4" fontSize="10.5" fill="var(--text-secondary)" fontFamily="monospace">
          estribo BE Ø{reinforcement.diam_stirrup_be}"
        </text>
      </g>
    </svg>
  );
}

/* ====================================================================== */
/*  P-M INTERACTION DIAGRAM                                                 */
/* ====================================================================== */

function PMDiagram({
  Pn,
  Mn,
  phi_Pn,
  phi_Mn,
  Po,
  Pu,
  Mu,
  DCR,
}: {
  Pn: number[];
  Mn: number[];
  phi_Pn: number[];
  phi_Mn: number[];
  Po: number;
  Pu: number;
  Mu: number;
  DCR: number;
}) {
  const W = 700;
  const H = 460;
  const PAD = 56;

  // Determine bounds — use Mn for X, Pn for Y
  const allM = [...Mn, ...phi_Mn, Mu, -Mu, 0];
  const allP = [...Pn, ...phi_Pn, Pu, 0, Po];
  const Mmin = Math.min(...allM);
  const Mmax = Math.max(...allM);
  const Pmin = Math.min(...allP);
  const Pmax = Math.max(...allP);
  const dM = Math.max(Math.abs(Mmin), Math.abs(Mmax)) * 1.1;
  const dP_lo = Pmin * 1.1;
  const dP_hi = Pmax * 1.1;

  const xOf = (m: number) => PAD + ((m + dM) / (2 * dM)) * (W - 2 * PAD);
  const yOf = (p: number) => H - PAD - ((p - dP_lo) / (dP_hi - dP_lo)) * (H - 2 * PAD);

  const pathNominal = Mn.map((m, i) => `${i === 0 ? 'M' : 'L'} ${xOf(m).toFixed(1)} ${yOf(Pn[i]).toFixed(1)}`).join(' ') + ' Z';
  const pathDesign = phi_Mn.map((m, i) => `${i === 0 ? 'M' : 'L'} ${xOf(m).toFixed(1)} ${yOf(phi_Pn[i]).toFixed(1)}`).join(' ') + ' Z';

  // Demand point — convert to (|Mu|, Pu) since the curve is symmetric for walls.
  // Plot both ±Mu so the user sees the demand point on the side it acts.
  const ptX = xOf(Mu);
  const ptY = yOf(Pu);

  // Grid lines
  const Pticks = niceTicks(dP_lo, dP_hi, 6);
  const Mticks = niceTicks(-dM, dM, 6);

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
      {/* Axes */}
      <line x1={PAD} y1={yOf(0)} x2={W - PAD} y2={yOf(0)} stroke="var(--border-default)" strokeWidth="1" />
      <line x1={xOf(0)} y1={PAD} x2={xOf(0)} y2={H - PAD} stroke="var(--border-default)" strokeWidth="1" />

      {/* Gridlines + tick labels */}
      {Pticks.map((p, i) => (
        <g key={`pt-${i}`}>
          <line x1={PAD} y1={yOf(p)} x2={W - PAD} y2={yOf(p)} stroke="var(--border-subtle)" strokeWidth="0.5" strokeDasharray="2 3" />
          <text x={PAD - 6} y={yOf(p) + 3} textAnchor="end" fontSize="9" fontFamily="monospace" fill="var(--text-muted)">
            {(p / 1000).toFixed(0)}
          </text>
        </g>
      ))}
      {Mticks.map((m, i) => (
        <g key={`mt-${i}`}>
          <line x1={xOf(m)} y1={PAD} x2={xOf(m)} y2={H - PAD} stroke="var(--border-subtle)" strokeWidth="0.5" strokeDasharray="2 3" />
          <text x={xOf(m)} y={H - PAD + 14} textAnchor="middle" fontSize="9" fontFamily="monospace" fill="var(--text-muted)">
            {(m / 1e5).toFixed(1)}
          </text>
        </g>
      ))}

      {/* Curves */}
      <path d={pathNominal} fill="rgba(59,130,246,0.06)" stroke="#3B82F6" strokeWidth="1.4" />
      <path d={pathDesign} fill="rgba(245,158,11,0.08)" stroke="#F59E0B" strokeWidth="1.6" />

      {/* Demand point (Mu, Pu) */}
      <circle cx={ptX} cy={ptY} r={6} fill="var(--danger)" stroke="white" strokeWidth="1.5" />
      <text x={ptX + 10} y={ptY - 8} fontSize="10" fontFamily="monospace" fill="var(--danger)" style={{ fontWeight: 700 }}>
        (Mu, Pu) — D/C = {DCR.toFixed(3)}
      </text>

      {/* Axis labels */}
      <text x={W - PAD + 4} y={yOf(0) - 6} fontSize="10" fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 700 }}>
        M (tonf·m)
      </text>
      <text x={xOf(0) + 8} y={PAD - 6} fontSize="10" fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 700 }}>
        P (tonf, + compresión)
      </text>

      {/* Legend */}
      <g transform={`translate(${W - 200}, ${PAD + 8})`}>
        <line x1="0" y1="0" x2="20" y2="0" stroke="#3B82F6" strokeWidth="1.4" />
        <text x="26" y="3" fontSize="10" fontFamily="monospace" fill="var(--text-secondary)">curva nominal Pn-Mn</text>
        <line x1="0" y1="16" x2="20" y2="16" stroke="#F59E0B" strokeWidth="1.6" />
        <text x="26" y="19" fontSize="10" fontFamily="monospace" fill="var(--accent)">curva de diseño φP-φM</text>
      </g>
    </svg>
  );
}

function niceTicks(min: number, max: number, target: number): number[] {
  if (!isFinite(min) || !isFinite(max) || min === max) return [];
  const range = max - min;
  const raw = range / target;
  const pow = Math.pow(10, Math.floor(Math.log10(Math.abs(raw))));
  const ratios = [1, 2, 2.5, 5, 10];
  const step = pow * (ratios.find((r) => r * pow >= raw) ?? 10);
  const start = Math.ceil(min / step) * step;
  const out: number[] = [];
  for (let v = start; v <= max + 1e-9; v += step) out.push(v);
  return out;
}
